import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Observable } from "rxjs";
import {
	WarningModalComponent,
	WarningModalData,
} from "../modals/warning-modal/warning-modal.component";
import { AppError, ErrorSeverity } from "./error-handler.service";

@Injectable({
	providedIn: "root",
})
export class NetworkErrorService {
	private readonly dialog = inject(MatDialog);

	private networkErrorSubject = new BehaviorSubject<boolean>(false);
	networkError$: Observable<boolean> = this.networkErrorSubject.asObservable();

	// Track if a network error modal is currently displayed to prevent duplicates
	private isNetworkErrorModalOpen = false;

	/**
	 * Handle network error and show modal dialog
	 * @param error The error to handle
	 */
	handleNetworkError(error: AppError): void {
		// Only display one network error modal at a time
		if (this.isNetworkErrorModalOpen) {
			return;
		}

		// Set network error state
		this.networkErrorSubject.next(true);

		// Show dialog
		const dialogRef = this.dialog.open<WarningModalComponent, WarningModalData>(
			WarningModalComponent,
			{
				width: "400px",
				data: {
					title: "Network Error",
					message: "Network error. Please check your internet connection.",
					confirmButtonText: "Retry",
					cancelButtonText: "Dismiss",
					confirmBtnCssClass: "btn-primary",
				},
				disableClose: false,
			},
		);

		this.isNetworkErrorModalOpen = true;

		// Handle dialog close
		dialogRef.afterClosed().subscribe((result) => {
			this.isNetworkErrorModalOpen = false;
			this.networkErrorSubject.next(false);

			// If user clicked retry, you can implement retry logic here
			if (result === true) {
				// Optional: Implement retry logic if needed
				// For example, you could emit an event that components can listen for
			}
		});
	}

	/**
	 * Check if an error is a network error (status code 0)
	 */
	isNetworkError(error: AppError): boolean {
		return error.statusCode === 0;
	}

	/**
	 * Capture network error from console error format
	 * @param args Console error arguments
	 */
	captureConsoleNetworkError(args: any[]): void {
		if (args && args.length >= 3 && args[0] === "%c[APP ERROR]") {
			const errorObj = args[2];

			if (
				errorObj &&
				errorObj.message ===
					"Network error. Please check your internet connection" &&
				errorObj.statusCode === 0
			) {
				const appError: AppError = {
					message: errorObj.message,
					originalError: errorObj.originalError,
					severity: ErrorSeverity.MAJOR,
					timestamp: errorObj.timestamp || new Date(),
					context: errorObj.context || {},
					statusCode: 0,
				};

				this.handleNetworkError(appError);
			}
		}
	}
}
